import React from "react";
import styles from "./DetailPanel.module.css";
import { IoClose } from "react-icons/io5";

const DetailPanel = ({ item, onClose, resultsFields2 }) => {
  if (!item || !resultsFields2) return null;

  const { title: titleKey, summary: summaryKey, ...otherFields } = resultsFields2;
  const dynamicMeta = item?._meta || [];

  const title = item[titleKey] || "No Title";
  const summary = item[summaryKey] || "";

  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (value === 0 || value === "0") return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;

    return false;
  };

  const formatLabel = (key) =>
    key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const isUrl = (value) => {
    if (typeof value !== "string") return false;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const isEmail = (value) =>
    typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());


  const toText = (value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };


  const renderDynamicValue = (key, value, compact = false) => {
    const text = toText(value);
    const displayText = compact && text.length > 160 ? `${text.slice(0, 160)}...` : text;

    if (key.toLowerCase().includes("address")) {
      return <span>{displayText}</span>;
    }

    if (isUrl(text)) {
      return (
        <a
          href={text}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.jobLink}
          onClick={(e) => e.stopPropagation()}
        >
          {displayText}
        </a>
      );
    }

    if (isEmail(text)) {
      return (
        <a href={`mailto:${text}`} className={styles.jobLink} onClick={(e) => e.stopPropagation()}>
          {displayText}
        </a>
      );
    }

    return <span>{displayText}</span>;
  };

  return (
    <div className={styles.detailPanel}>
      <button className={styles.closeButton} onClick={onClose} aria-label="Close">
        <IoClose className={styles.closeIcon} />
      </button>

      {/* Title */}
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.metaCapsules}>
        {dynamicMeta.map((f, idx) => {
          const Icon = f.icon;

          return (
            <span key={idx} className={styles.capsule}>
              {Icon && <Icon className={styles.icon} />}
              {f.value}
            </span>
          );
        })}
      </div>

      {/* Summary */}
      {summary && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Description</h3>
          <div className={styles.description}>
            {renderDynamicValue(
              summaryKey,
              summary
                .replace(/\r\n/g, "\n") // Normalize Windows line endings
                .split("\n") // Split into lines
                .map((line) => line.trimEnd()) // Remove trailing spaces from each line
                .join("\n") // Rejoin lines
                .replace(/\n{2,}/g, "\n\n") // Collapse multiple blank lines to just one blank line
                .trim(), // Remove leading/trailing whitespace
            )}
          </div>
        </div>
      )}

      <hr className={styles.divider} />

      {/* Other fields from resultsFields2 */}
      <div className={styles.fieldsList}>
        {Object.entries(otherFields).map(([labelKey, fieldKey]) => {
          const value = item[fieldKey];
          if (isEmptyValue(value)) return null;

          const isArray = Array.isArray(value);

          return (
            <div key={fieldKey} className={styles.fieldRow}>
              <div className={styles.fieldLeft}>{formatLabel(labelKey)}</div>
              <div className={styles.fieldRight}>
                {isArray ? (
                  <div className={styles.chipContainer}>
                    {value.map((v, i) => (
                      <span key={i} className={styles.chip}>
                        {renderDynamicValue(fieldKey, v)}
                      </span>
                    ))}
                  </div>
                ) : (
                  renderDynamicValue(fieldKey, value)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DetailPanel;